import { createFileRoute, notFound } from "@tanstack/react-router"
import { Activity, Bot, Coins, Inbox, Palette, Plus, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ApiError } from "@/api/client"
import { CodeBlock } from "@/components/app/code-block"
import { useConfirm } from "@/components/app/confirm-dialog"
import { CopyButton } from "@/components/app/copy-button"
import { createColumns, DataTable } from "@/components/app/data-table"
import { TagInput } from "@/components/app/fields"
import { Shortcut } from "@/components/app/kbd"
import { KeyValueList } from "@/components/app/key-value"
import { Markdown } from "@/components/app/markdown"
import { PageContainer, PageHeader, PageSection, Panel } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { SecretReveal } from "@/components/app/secret-reveal"
import { StatCard } from "@/components/app/stat-card"
import { EmptyState, ErrorState, LoadingState, TableSkeleton } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { UserAvatar } from "@/components/app/user-avatar"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"

/**
 * Living reference of the design-system layer (`src/components/app`).
 * URL-only (`/kit`), development builds only. Sample content is intentionally
 * not translated — this page is for developers.
 */
export const Route = createFileRoute("/_app/kit")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound()
  },
  component: KitPage,
})

const SAMPLE_MD = `### Incident summary

The **checkout API** returned \`502\` for 4 minutes after a config change.

| Step | Owner | Status |
| --- | --- | --- |
| Roll back | Planner | done |
| Post-mortem | Writer | in progress |

\`\`\`ts
export async function rollback(id: string) {
  await api.post(\`/deployments/\${id}/rollback\`)
}
\`\`\`
`

interface Row {
  id: number
  agent: string
  status: "running" | "done" | "failed"
  tokens: number
  at: string
}

const rows: Row[] = Array.from({ length: 12 }, (_, i) => ({
  id: i + 1,
  agent: ["Planner", "Researcher", "Analyst", "Writer"][i % 4]!,
  status: (["running", "done", "failed"] as const)[i % 3]!,
  tokens: 1200 + i * 731,
  at: new Date(Date.now() - i * 37 * 60_000).toISOString(),
}))

function KitPage() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const [tags, setTags] = useState(["md", "pdf"])
  const [streamed, setStreamed] = useState("")

  // simulate a streaming answer to show Markdown's incomplete-markdown handling
  useEffect(() => {
    let i = 0
    const id = setInterval(() => {
      i = Math.min(SAMPLE_MD.length, i + 12)
      setStreamed(SAMPLE_MD.slice(0, i))
      if (i >= SAMPLE_MD.length) clearInterval(id)
    }, 60)
    return () => clearInterval(id)
  }, [])

  const columns = useMemo(
    () =>
      createColumns<Row>((c) => [
        c.accessor("agent", {
          header: "Agent",
          cell: ({ getValue }) => (
            <span className="flex items-center gap-2">
              <UserAvatar name={getValue()} size="sm" className="size-5" />
              {getValue()}
            </span>
          ),
        }),
        c.accessor("status", {
          header: "Status",
          cell: ({ getValue }) => (
            <StatusBadge
              tone={getValue() === "done" ? "success" : getValue() === "failed" ? "danger" : "info"}
              dot
              pulse={getValue() === "running"}
            >
              {getValue()}
            </StatusBadge>
          ),
        }),
        c.accessor("tokens", { header: "Tokens", meta: { align: "end" } }),
        c.accessor("at", {
          header: "Started",
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} />,
        }),
      ]),
    [],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Design system"
        description="Building blocks in src/components/app. Use these before reaching for raw components/ui."
        icon={Palette}
        breadcrumbs={[{ label: "Developers" }, { label: "Kit" }]}
        actions={
          <>
            <Button variant="outline">Secondary</Button>
            <Button>
              <Plus />
              Primary
            </Button>
          </>
        }
      />
      <div className="space-y-12">
        <PageSection title="Stat cards">
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              label="Runs today"
              value="1,284"
              icon={Activity}
              delta={12.4}
              deltaLabel="vs yesterday"
            />
            <StatCard label="Spend" value="€ 42.18" icon={Coins} delta={-3.1} deltaLabel="vs yesterday" />
            <StatCard label="Agents" value="18" icon={Bot} loading />
          </div>
        </PageSection>

        <PageSection title="Badges, avatars, shortcuts">
          <Panel>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge>neutral</StatusBadge>
              <StatusBadge tone="accent">accent</StatusBadge>
              <StatusBadge tone="success" dot>
                success
              </StatusBadge>
              <StatusBadge tone="warning">warning</StatusBadge>
              <StatusBadge tone="danger">danger</StatusBadge>
              <StatusBadge tone="info" dot pulse>
                live
              </StatusBadge>
              <span className="mx-2 h-5 w-px bg-border" />
              <UserAvatar name="Ada Lovelace" size="sm" />
              <UserAvatar name="Grace Hopper" />
              <UserAvatar name="Linus" size="lg" />
              <span className="mx-2 h-5 w-px bg-border" />
              <Shortcut keys={["mod", "k"]} />
              <Shortcut keys={["g", "s"]} sequence />
              <CopyButton value="copied from the kit" />
            </div>
          </Panel>
        </PageSection>

        <PageSection
          title="Data table"
          description="Client-side sorting, search, column toggle, pagination and row actions."
        >
          <DataTable
            data={rows}
            columns={columns}
            getRowId={(r) => String(r.id)}
            searchable
            columnToggle
            pageSize={5}
            rowActions={() => (
              <DropdownMenuItem
                variant="destructive"
                onClick={() =>
                  void confirm({
                    title: "Delete run?",
                    description: "Just a demo.",
                    destructive: true,
                    confirmText: "delete",
                  })
                }
              >
                <Trash2 />
                Delete
              </DropdownMenuItem>
            )}
          />
        </PageSection>

        <PageSection
          title="Markdown (streaming)"
          description="streamdown + shiki; tolerates half-written markdown."
        >
          <Panel>
            <Markdown streaming={streamed.length < SAMPLE_MD.length}>{streamed}</Markdown>
          </Panel>
        </PageSection>

        <PageSection title="Code, key-value, secret">
          <div className="grid gap-4 lg:grid-cols-2">
            <CodeBlock
              code={JSON.stringify({ timezone: ["UTC", "Europe/Amsterdam"] }, null, 2)}
              language="json"
            />
            <Panel>
              <KeyValueList
                items={[
                  { label: "Run ID", value: "run_8f2c1a", mono: true, copy: "run_8f2c1a" },
                  { label: "Model", value: "claude-sonnet" },
                  { label: "Started", value: <RelativeTime value={rows[3]!.at} /> },
                ]}
              />
            </Panel>
            <SecretReveal secret="mit_demo_9b4f0e2c7d1a" masked className="lg:col-span-2" />
          </div>
        </PageSection>

        <PageSection title="Tag input">
          <Panel>
            <TagInput value={tags} onChange={setTags} placeholder="Add extensions…" />
          </Panel>
        </PageSection>

        <PageSection title="States">
          <div className="grid gap-4 lg:grid-cols-2">
            <EmptyState
              icon={Inbox}
              title="No runs yet"
              description="Runs appear here once an agent starts working."
              action={
                <Button size="sm">
                  <Plus />
                  {t("common.retry")}
                </Button>
              }
            />
            <ErrorState error={new ApiError(0, "network_error", "offline")} onRetry={() => {}} />
            <LoadingState />
            <TableSkeleton rows={3} />
          </div>
        </PageSection>
      </div>
    </PageContainer>
  )
}
